import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConsent } from "../../context/ConsentContext";
import { identifierPairs, hasDpiit } from "../../config/statutoryIdentifiers";
import NewsletterSignup from "../NewsletterSignup/NewsletterSignup";

// Image imports
import footerBg from "../../images/bg/footer-bg.png";
import emailIcon from "../../images/icon/email-icon.svg";
import locationIcon from "../../images/icon/location-icon.svg";
import callIcon from "../../images/icon/call-icon.svg";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { openSettings } = useConsent();

  const arrowIcon = (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="5.06641"
        y="19.2783"
        width="20.5712"
        height="2.61221"
        transform="rotate(-40.2798 5.06641 19.2783)"
        fill="white"
      />
      <rect
        x="7.97144"
        y="6.54443"
        width="2.61221"
        height="2.61221"
        transform="rotate(-40.2798 7.97144 6.54443)"
        fill="white"
      />
      <rect
        x="11.6528"
        y="6.84814"
        width="2.61221"
        height="2.61221"
        transform="rotate(-40.2798 11.6528 6.84814)"
        fill="white"
      />
      <rect
        x="15.3345"
        y="7.15186"
        width="2.61221"
        height="2.61221"
        transform="rotate(-40.2798 15.3345 7.15186)"
        fill="white"
      />
      <rect
        x="18.7124"
        y="11.1372"
        width="2.61221"
        height="2.61221"
        transform="rotate(-40.2798 18.7124 11.1372)"
        fill="white"
      />
      <rect
        x="18.4089"
        y="14.8198"
        width="2.61221"
        height="2.61221"
        transform="rotate(-40.2798 18.4089 14.8198)"
        fill="white"
      />
      <rect
        x="18.1045"
        y="18.501"
        width="2.61221"
        height="2.61221"
        transform="rotate(-40.2798 18.1045 18.501)"
        fill="white"
      />
    </svg>
  );

  // Social links
  const socials = [
    {
      icon: <i className="fa-brands fa-facebook"></i>,
      name: "Facebook",
      url: "https://www.facebook.com/axoixos",
    },
    {
      icon: <i className="fa-brands fa-instagram"></i>,
      name: "Instagram",
      url: "https://www.instagram.com/axoixos",
    },
    {
      icon: <i className="fa-brands fa-linkedin"></i>,
      name: "LinkedIn",
      url: "https://www.linkedin.com/showcase/axoix",
    },
    {
      icon: <i className="fa-brands fa-x-twitter"></i>,
      name: "X (Twitter)",
      url: "https://x.com/AXOIX_IN",
    },
    {
      icon: <i className="fa-brands fa-square-youtube"></i>,
      name: "YouTube",
      url: "https://www.youtube.com/@AXOIXOS",
    },
  ];

  return (
    <footer
      className="footer footer-style-one pt-145 bg_img"
    >
      <div className="xb-footer-wrap">
        {/* Footer Heading */}
        <div className="xb-footer-heading wow zoomIn" data-wow-delay="0ms" data-wow-duration="600ms">
          {/* Decorative wordmark, not a heading — as an <h1> it gave every page a
              second <h1> alongside the real page heading. Styling lives on
              .xb-footer-heading .title, which no longer relies on h1 inheritance. */}
          <div className="title">AXOIX</div>
          <a className="mail" href="mailto:hello@axoix.com">
            <img src={emailIcon} alt="email" /> hello@axoix.com
          </a>
        </div>

        {/* Newsletter Signup */}
        <div className="xb-footer-newsletter" style={{ marginBottom: 60, textAlign: "center" }}>
          <NewsletterSignup source="footer" variant="footer" />
          <a href="/feed.xml" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 18, fontSize: 13, color: "var(--color-primary)" }}>
            <i className="fas fa-rss"></i> Subscribe via RSS
          </a>
        </div>

        {/* Footer Navigation */}
        <div className="xb-footer-nav">
          <div className="xb-footer-nav-item">
            <span className="sub-title">{t('footer.featuresLabel')}</span>
            <h2 className="title">
              <Link to="/service">{t('footer.features')}</Link>
            </h2>
          </div>
          <div className="xb-footer-nav-item">
            <span className="sub-title">{t('footer.aboutLabel')}</span>
            <h2 className="title">
              <Link to="/about">{t('footer.about')}</Link>
            </h2>
          </div>
          <div className="xb-footer-nav-item">
            <span className="sub-title">{t('footer.pricingLabel')}</span>
            <h2 className="title">
              <Link to="/pricing">{t('footer.pricing')}</Link>
            </h2>
          </div>
          <div className="xb-footer-nav-item">
            <span className="sub-title">{t('footer.charactersLabel')}</span>
            <h2 className="title">
              <Link to="/team">{t('footer.characters')}</Link>
            </h2>
          </div>
          <div className="xb-footer-nav-item">
            <span className="sub-title">{t('footer.contactLabel')}</span>
            <h2 className="title">
              <Link to="/contact">{t('footer.contact')}</Link>
            </h2>
          </div>
        </div>

        {/* Social Media Section */}
        <div className="xb-social-media-wrap">
          {socials.map((social, idx) => (
            <div className="xb-social-media-item ul_li_between" key={idx}>
              <div className="xb-item--holder ul_li">
                <div className="xb-item--icon">{social.icon}</div>
                <span className="xb-item--name">{social.name}</span>
              </div>

              <span className="xb-item--arrow">{arrowIcon}</span>

              <a
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                className="xb-overlay"
              ></a>
            </div>
          ))}
        </div>

        {/* Legal Links */}
        <div className="xb-footer-legal">
          <div className="legal-links-wrap">
            <Link to="/terms">{t('footer.legal.terms')}</Link>
            <span className="separator">|</span>
            <Link to="/privacy">{t('footer.legal.privacy')}</Link>
            <span className="separator">|</span>
            <Link to="/cookies">{t('footer.legal.cookies')}</Link>
            <span className="separator">|</span>
            <Link to="/refund">{t('footer.legal.refund')}</Link>
            <span className="separator">|</span>
            <Link to="/faq">{t('footer.legal.faq')}</Link>
            <span className="separator">|</span>
            <Link to="/compare">{t('footer.legal.compare')}</Link>
            <span className="separator">|</span>
            <Link to="/careers">{t('footer.legal.careers')}</Link>
            <span className="separator">|</span>
            <Link to="/blog">{t('footer.legal.blog')}</Link>
            <span className="separator">|</span>
            <Link to="/become-partner">{t('footer.legal.partner')}</Link>
            <span className="separator">|</span>
            <button
              onClick={openSettings}
              className="cookie-settings-btn"
            >
              {t('footer.legal.cookieSettings')}
            </button>
          </div>
        </div>

        {/* Statutory credentials. Only the DPIIT sentence is translated — the
            identifiers are legal strings and must read identically in every
            locale, so they are not routed through i18n.

            Values come from src/config/statutoryIdentifiers.ts. Setting `dpiit`
            to null there drops both the badge and the DPIIT pair from this row,
            with no edit here. */}
        <div className="xb-footer-credentials">
          {hasDpiit() && (
            <span className="credential-badge">{t('footer.credentials.dpiit')}</span>
          )}
          {identifierPairs().map(([label, value], i) => (
            <React.Fragment key={label}>
              {i > 0 && <span className="separator">|</span>}
              <span className="credential-id">
                {label}&nbsp;{value}
              </span>
            </React.Fragment>
          ))}
        </div>


        {/* Footer Bottom */}
        <div className="xb-footer-bottom">
          <div className="contact-item">
            <img
              src={process.env.PUBLIC_URL + "/images/ptbi.png"}
              alt="Proud to be an Indian"
              className="ptbi-inline-img"
            />
            <img src={locationIcon} alt="location" />
            <span className="contact-method">{t('footer.location')}</span>
          </div>

          <div className="contact-item copyright-item">
            <p>
              {t('footer.copyright')} <a href="https://axoix.com" target="_blank" rel="noopener noreferrer">{t('footer.company')}</a> © 2025 - 2026
            </p>
          </div>

          <div className="contact-item">
            <a href="mailto:hello@axoix.com">
              <img src={callIcon} alt="contact" />
            </a>
            <a className="contact-method" href="mailto:hello@axoix.com">
              hello@axoix.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
